import React, { forwardRef, useRef, useImperativeHandle, useState, Fragment } from "react";
import { useGlobalValue } from "colbi_web_ui/lib/state/GlobalProvider";
import styles from "./FileSubmissionForm.module.sass";
import {
    SovosAutocomplete,
    SovosTextField,
    SovosInputAdornment,
    SovosIconButton,
    SovosCheckboxField,
    SovosBox,
    SovosTypography,
    SovosButton,
    SovosDivider,
} from "mosaic-react";

import { Visibility, VisibilityOff } from "mosaic-react-icons";

const FileSubmissionForm = (props, ref) => {
    const { data, status, onChange, onSave, projectData } = props;
    const [showPassword, setShowPassword] = useState(false);
    const handleClickShowPassword = () => setShowPassword(!showPassword);

    const {
        filenames,
        fileIds,
        projectId,
        filesWithProblems,
        filePeriods,
        userId,
        validate = false,
    } = data || {};

    const taxRegistrationNumber = (projectData && projectData["taxRegistrationNumber"]) || "";
    const formRef = useRef();

    function monthName(mon) {
        return [
            "January",
            "February",
            "March",
            "April",
            "May",
            "June",
            "July",
            "August",
            "September",
            "October",
            "November",
            "December",
        ][mon - 1];
    }

    const { i18n } = useGlobalValue();

    const monthArray = Array.from(Array(13), (_, x) => {
        return { id: x, name: monthName(x) };
    }).filter((elem) => elem.id !== 0);

    const handleChange = (e, prop, value) => {
        onChange &&
            onChange({
                originalEvent: e,
                target: {
                    value: {
                        data: {
                            ...data,
                            [prop]: value,
                        },
                    },
                },
            });
    };

    useImperativeHandle(ref, () => ({
        submit: () => {
            formRef.current.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
        },
    }));

    const selectedPeriod = monthArray
        ? monthArray.find(({ id }) => id === parseInt((data || {}).period))
        : null;
    return status !== "loading" ? (
        <form ref={formRef} autoComplete="off">
            <SovosBox
                sx={{
                    backgroundColor: "#f5f8fc",
                    borderRadius: 2,
                    border: "1px solid #dce4ef",
                    display: "flex",
                    flexDirection: "column",
                    maxHeight: "28vh",
                    overflow: "scroll",
                    padding: "unset",
                }}
            >
                <SovosBox
                    sx={{
                        position: "sticky",
                        top: 0,
                        left: 0,
                        zIndex: 999,
                        backgroundColor: "#f5f8fc",
                        width: "100%",
                        borderTopLeftRadius: "8px",
                        borderTopRightRadius: "8px",
                        padding: "16px 16px 0 16px",
                    }}
                >
                    <SovosTypography variant="subtitle1" children={i18n`Files to Submit`} />
                    <SovosDivider />
                </SovosBox>
                <SovosBox sx={{ padding: 2 }}>
                    {(filenames || []).map((fileName, index) => (
                        <Fragment
                            sx={{
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                            key={index}
                        >
                            <SovosTypography>{fileName}</SovosTypography>
                        </Fragment>
                    ))}
                </SovosBox>
            </SovosBox>
            <SovosBox className={styles["input-field"]}>
                <SovosTypography variant="h6" sx={{ marginTop: 2, marginBottom: 2 }}>
                    {i18n`Enter User ID (optional)`}
                </SovosTypography>
                <SovosBox className={styles["content-flex"]}>
                    <SovosTypography sx={{ marginTop: 2.5 }}>
                        {taxRegistrationNumber}
                    </SovosTypography>
                    <SovosTypography sx={{ marginTop: 2.5 }}>/</SovosTypography>
                    <SovosBox>
                        <SovosTextField
                            type="number"
                            variant="filled"
                            InputProps={{ inputProps: { min: 0 } }}
                            placeholder={i18n`Enter User ID (e.g. 1)`}
                            onChange={(e) => {
                                handleChange(e, "userId", e.target.value);
                            }}
                        />
                    </SovosBox>
                </SovosBox>

                <SovosTextField
                    label="Tax Authority Password"
                    variant="filled"
                    required
                    type={showPassword ? "text" : "password"}
                    onChange={(e) => {
                        handleChange(e, "userPassword", e.target.value);
                    }}
                    fullWidth // Optionally make the input take up the full width of its container
                    InputProps={{
                        endAdornment: (
                            <SovosInputAdornment position="end">
                                <SovosIconButton
                                    tooltipText={i18n`Show`}
                                    aria-label="toggle password visibility"
                                    onClick={handleClickShowPassword}
                                    edge="end"
                                >
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                </SovosIconButton>
                            </SovosInputAdornment>
                        ),
                    }}
                />
                <SovosAutocomplete
                    value={selectedPeriod}
                    onChange={(event, newValue) => {
                        // Call handleChange with the selected option's ID
                        handleChange(event, "period", newValue?.id || null);
                    }}
                    options={monthArray || []}
                    getOptionLabel={(option) => option.name || ""} // Use the 'name' field for display
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    renderInput={(params) => (
                        <SovosTextField
                            {...params}
                            label="Select a Period" // Placeholder equivalent
                            required
                            onChange={(e) => {
                                handleChange(e, "period", e.target.value.id);
                            }}
                        />
                    )}
                    filterSelectedOptions
                />

                <SovosCheckboxField
                    label="Validate File"
                    onChange={(e) => {
                        handleChange(e, "validate", e.target.checked ? 1 : 0);
                    }}
                ></SovosCheckboxField>

                <SovosBox sx={{ display: "flex", justifyContent: "flex-end", marginBottom: -5 }}>
                    <SovosButton
                        variant="contained"
                        color="primary"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (formRef.current.checkValidity()) {
                                data["__typename"] = "DataFileSubmission";
                                data["dataFileIds"] = fileIds;
                                data["projectId"] = projectId;
                                data["filesWithProblems"] = filesWithProblems || [];
                                data["filePeriods"] = filePeriods || [];
                                data["userId"] = userId || "";
                                data["validate"] = validate || 0;
                                onSave && onSave();
                            } else {
                                formRef.current.reportValidity();
                            }
                        }}
                    >
                        Submit
                    </SovosButton>
                </SovosBox>
            </SovosBox>
        </form>
    ) : null;
};

const FileSubmissionFormRef = forwardRef(FileSubmissionForm);

FileSubmissionFormRef.queries = (args) => {
    return [
        {
            resource: "project",
            args: { targetProjectId: args.projectId },
            body: "__typename,id,taxRegistrationNumber",
        },
    ];
};

FileSubmissionFormRef.dataMapper = ["projectData"];

export default FileSubmissionFormRef;
